import React from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import Switch from '@mui/material/Switch';
import ModalButtons from 'src/components/UI/button/ModalButtons';
import { APICONSTANTS } from 'src/components/Constants';

import axios from 'src/axios';
import {
  Box,
  MenuItem,
  Select,
  TextField,
  IconButton,
  Tooltip,
  InputAdornment
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import { FormControl } from '@mui/material';
import { SnackContext, AppContext, ThemeContext } from 'src/store/ContextStore';

import {
  getDependencies,
  getClinicalDefaults,
  saveClinicalDefault,
  deleteClinicalDefault,
  REFERRAL_REASONS,
  REFERRAL_TRIAGE
} from './formService';
import TextInput from 'src/components/UI/input/TextInput';
import AutocompleteInput from 'src/components/UI/input/AutocompleteInput';
import UploadFileList from 'src/components/UI/input/UploadFileList';
import ClinicalNoteDeleteModal from '../../diagnoses-menu/ClinicalNoteDeleteModal';
import FormLabel from 'src/components/UI/input/CustomFormLabel';
import DateInput from 'src/components/UI/input/DateInput';
import { calculateAge } from 'src/util/util';
import { useClinicalSuggestions } from 'src/hooks/useClinicalSuggestions';
import ClinicalSuggestionsPanel from 'src/components/UI/ClinicalSuggestionsPanel';

export default function ReferralForm({
  data,
  handleClose,
  familyMemberId,
  isProband = false,
  isEditing,
  model,
  isDisabled,
  setUnsavedChanges
}) {
  const { theme } = React.useContext(ThemeContext);

  const { content } = React.useContext(AppContext);
  const { pedigreeSelectedUsers: users } = content;
  const { selectedUser: user } = users;

  const addDays = (date, days) => {
    const d = new Date(date || new Date());
    d.setDate(d.getDate() + days);
    return d;
  };

  const patientDob = data?.dob;
  const computeAgeAtRecord = React.useCallback(
    (dateValue) => {
      if (!patientDob || !dateValue) return '';
      const parsedDate = new Date(dateValue);
      if (Number.isNaN(parsedDate.getTime())) return '';
      return calculateAge(patientDob, parsedDate);
    },
    [patientDob]
  );

  const baseReferralDate = isEditing
    ? model?.referralDate || new Date()
    : new Date();

  const defaultReferralDate = new Date();
  const defaultValues = isEditing
    ? { ...model }
    : {
        familyId: user.familyId,
        familyMemberId: familyMemberId,
        referralTo: '',
        externalReferralId: '',
        referralDate: defaultReferralDate,
        referredBy: '',
        referralReason: '',
        referralTriage: '',
        referralStatus: 'Open', // Open, Closed
        referralType: 'None',
        fileAttachments: [], // Make this form
        referralNotes: '',
        showInFamily: false,
        dateClosed: null,
        reasonForClosure: '',
        reminderEnabled: false,
        reminderDate: addDays(baseReferralDate, 14),
        ageAtRecord: computeAgeAtRecord(defaultReferralDate)
      };
  const [formValues, setFormValues] = React.useState(defaultValues);
  const [reminderTouched, setReminderTouched] = React.useState(false);

  const [typeOptions, setTypeOptions] = React.useState([]);
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const [error, setError] = React.useState({});

  React.useEffect(() => {
    getClinicalDefaults('referralTypes').then(setTypeOptions);
  }, []);

  React.useEffect(() => {
    if (!patientDob) return;
    const nextAge = computeAgeAtRecord(formValues.referralDate);
    setFormValues((prev) => {
      const currentAge = prev.ageAtRecord === undefined ? '' : prev.ageAtRecord;
      if (nextAge === '' && currentAge === '') return prev;
      if (nextAge === currentAge) return prev;
      return { ...prev, ageAtRecord: nextAge };
    });
  }, [patientDob, computeAgeAtRecord, formValues.referralDate]);

  React.useEffect(() => {
    if (!formValues.referralDate) return;
    if (formValues.reminderEnabled && !reminderTouched) {
      setFormValues((prev) => ({
        ...prev,
        reminderDate: addDays(formValues.referralDate, 14)
      }));
    }
  }, [formValues.referralDate]);

  const handleChange = (e) => {
    if (e.target.name === 'referralDate') {
      const nextDate = e.target.value;
      setFormValues((prevValues) => ({
        ...prevValues,
        ageAtRecord: computeAgeAtRecord(nextDate)
      }));
    }

    if (e.target.type === 'date') {
      const selectedDate = new Date(e.target.value);
      const today = new Date();

      if (selectedDate >= today) {
        setError((prev) => ({ ...prev, [e.target.name]: true }));
      } else setError({});
    }

    setFormValues((prevValues) => ({
      ...prevValues,
      [e.target.name]: e.target.value
    }));
    setUnsavedChanges((prev) => ({ ...prev, edit: true }));
  };

  const { setSnack } = React.useContext(SnackContext);

  const {
    suggestions,
    analyzingNote,
    selectedSuggestions,
    savingNote,
    handleAttachmentText,
    toggleSuggestion,
    acceptSuggestions,
    analyzeText
  } = useClinicalSuggestions({ familyMemberId, isProband });

  //Sure about this! Will be good for show in Family
  const onToggleSwitch = (e) => {
    setUnsavedChanges((prev) => ({ ...prev, edit: true }));
    setFormValues((prevValues) => ({
      ...prevValues,
      [e.target.name]: e.target.checked
    }));
    if (familyMemberId == 'none') {
      setFormValues((prevValues) => ({
        ...prevValues,
        showInFamily: true
      }));
    }
  };

  const handleCloseModal = () => {
    setOpen(false);
  };
  const handleOpenModal = () => {
    setOpen(true);
  };

  const deleteNote = async () => {
    setLoading(true);

    const dep = await getDependencies(
      user.familyId,
      model._id,
      theme.dateFormat
    );
    if (dep.length !== 0) {
      setSnack({
        status: true,
        msg: `Associated record exists; check the ${dep[0]}`,
        severity: 'error'
      });
      setLoading(false);

      return;
    }
    axios
      .delete(`/clinical-notes/note`, {
        params: {
          _id: model._id
        }
      })
      .then(() => {
        setSnack({
          status: true,
          msg: 'Record deleted',
          severity: 'success'
        });
        handleCloseModal();

        handleClose({ exit: true });
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: `Delete failed! ${err}`,
          severity: 'error'
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const validateForm = (values) => {
    let errors = {};

    if (values.referralDate === '') {
      errors.referralDate = 'Referral Date is required';
    }
    if (values.referredBy === '') {
      errors.referredBy = 'Referred By is required';
    }

    if (values.referralDate) {
      const selectedDate = new Date(values.dateTreferralDateaken);
      const today = new Date();

      if (selectedDate > today) {
        errors.referralDate = 'Date Taken cannot be in future';
      }
    }

    setError(errors);
    return errors;
  };

  //Think about this
  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const errors = validateForm(formValues);

    if (Object.keys(errors).length > 0) return Promise.reject();

    const payload = {
      ...formValues,
      reminderEnabled: formValues.reminderEnabled,
      reminderDate: formValues.reminderEnabled ? formValues.reminderDate : null
    };

    return axios
      .put(APICONSTANTS.referral, payload)
      .then((res) => {
        handleClose({ exit: true });
        if (res.status === 200) {
          setSnack({
            status: true,
            msg: 'Successfully saved',
            severity: 'success'
          });
        } else {
          setSnack({
            status: true,
            msg: `Upload failed!`,
            severity: 'error'
          });
        }
      })
      .catch((err) => {
        setSnack({
          status: true,
          msg: `Upload failed! ${err}`,
          severity: 'error'
        });
      });
  };

  return (
    //Should be fine to reuse old class?

    <form className="user-form">
      <ClinicalNoteDeleteModal
        loading={loading}
        open={open}
        handleCloseModal={handleCloseModal}
        onConfirm={deleteNote}
        title="Are you sure you want to delete this Referral ?"
        content="Warning: This action will permanently delete this Referral. Once deleted, this
        information will be permanently removed and cannot be restored."
      />
      <Grid container spacing={2}>
        <Grid size={12}>
          <TextInput
            label="Referral To"
            variant="outlined"
            fullWidth
            onChange={handleChange}
            name="referralTo"
            value={formValues.referralTo}
            InputProps={{ readOnly: isDisabled }}
          />
        </Grid>

        <Grid size={6}>
          <Grid sx={{ paddingBlock: 0.25 }}>
            <FormLabel>Referral Type</FormLabel>
          </Grid>
          <AutocompleteInput
            name="referralType"
            value={formValues.referralType || ''}
            onChange={(newValue) => {
              const label = newValue ? newValue.label || newValue : '';
              handleChange({ target: { name: 'referralType', value: label } });
              if (label) {
                saveClinicalDefault('referralTypes', label).then(
                  setTypeOptions
                );
              }
            }}
            options={typeOptions}
            onDelete={(option) => {
              deleteClinicalDefault('referralTypes', option.label).then(
                setTypeOptions
              );
            }}
            helperText="Please select or enter a referral type"
            disabled={isDisabled}
          />
        </Grid>

        <Grid size={6}>
          <Grid sx={{ paddingBlock: 0.25 }}>
            <FormLabel required>Referral Date</FormLabel>
          </Grid>
          <DateInput
            value={formValues.referralDate}
            liveText
            disableFuture
            onChange={(val) =>
              handleChange({
                target: { name: 'referralDate', value: val, type: 'date' }
              })
            }
            variant="outline"
            error={error?.referralDate}
            helperText={'Date cannot be in the future'}
            readOnly={isDisabled}
          />
        </Grid>

        <Grid size={6}>
          <TextInput
            label="Referred By"
            variant="outlined"
            fullWidth
            onChange={handleChange}
            name="referredBy"
            value={formValues.referredBy}
            required={true}
            error={!!error.referredBy}
            helperText={error.referredBy}
            InputProps={{ readOnly: isDisabled }}
          />
        </Grid>
        <Grid size={6}>
          <TextInput
            label="Age at Referral"
            name="ageAtRecord"
            type="number"
            value={formValues.ageAtRecord ?? ''}
            inputProps={{ min: 0, className: 'no-spinners' }}
            // Disabled (muted) only when an age is actually being
            // auto-calculated, i.e. DoB AND referral date both set.
            // Editable when either is missing so a known age can be
            // recorded directly.
            disabled={!!patientDob && !!formValues.referralDate}
            onChange={
              patientDob && formValues.referralDate
                ? undefined
                : (e) => {
                    const val = e.target.value;
                    const parsed = val === '' ? '' : Number(val);
                    if (val === '' || !Number.isNaN(parsed)) {
                      setFormValues((prev) => ({
                        ...prev,
                        ageAtRecord: val === '' ? '' : parsed
                      }));
                      setUnsavedChanges((prev) => ({ ...prev, edit: true }));
                    }
                  }
            }
          />
        </Grid>

        <Grid size={6}>
          <TextInput
            label="Referral Reason"
            variant="outlined"
            fullWidth
            type="autocomplete"
            options={REFERRAL_REASONS}
            onChange={(e) => {
              const label = e ? e.label : '';
              handleChange({
                target: { name: 'referralReason', value: label }
              });
            }}
            name="referralReason"
            value={formValues.referralReason}
            readOnly={isDisabled}
          />
        </Grid>

        <Grid size={6}>
          <TextInput
            label="Referral Triage"
            variant="outlined"
            fullWidth
            type="select"
            options={REFERRAL_TRIAGE}
            onChange={handleChange}
            name="referralTriage"
            value={formValues.referralTriage}
            disabled={isDisabled}
          />
        </Grid>

        <Grid size={6}>
          <Grid sx={{ paddingBlock: 0.5 }}>
            <FormLabel>Referral Status</FormLabel>
          </Grid>
          <Box sx={{ display: 'flex', position: 'relative' }}>
            <FormControl fullWidth>
              <Select
                style={{ width: '100%' }}
                name="referralStatus"
                value={formValues.referralStatus}
                onChange={handleChange}
                readOnly={isDisabled}
              >
                {['Open', 'Closed'].map((option) => (
                  <MenuItem value={option} key={option} onClick={handleChange}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Grid>

        {formValues.referralStatus === 'Closed' && (
          <>
            <Grid size={6}>
              <Grid sx={{ paddingBlock: 0.5 }}>
                <FormLabel>Date Closed</FormLabel>
              </Grid>
              <DateInput
                name="dateClosed"
                value={formValues.dateClosed}
                onChange={(e) =>
                  setFormValues((prevValues) => ({
                    ...prevValues,
                    dateClosed: e
                  }))
                }
                variant="outline"
                minDate={
                  isEditing ? new Date(1600, 0, 0) : formValues.referralDate
                }
                readOnly={isDisabled}
              />
            </Grid>

            <Grid size={12}>
              <TextInput
                label="Reason For Closure"
                variant="outlined"
                fullWidth
                onChange={handleChange}
                name="reasonForClosure"
                value={formValues.reasonForClosure}
                multiline
                InputProps={{ readOnly: isDisabled }}
              />
            </Grid>
          </>
        )}

        <Grid size={6}>
          <TextInput
            label="External Referral ID"
            variant="outlined"
            fullWidth
            onChange={handleChange}
            name="externalReferralId"
            value={formValues.externalReferralId}
            InputProps={{ readOnly: isDisabled }}
          />
        </Grid>

        <Grid size={12}>
          <TextInput
            label="Referral Notes"
            variant="outlined"
            fullWidth
            onChange={handleChange}
            name="referralNotes"
            value={formValues.referralNotes}
            multiline
            InputProps={{
              readOnly: isDisabled,
              endAdornment: formValues.referralNotes ? (
                <InputAdornment position="end" sx={{ alignSelf: 'center' }}>
                  <Tooltip title="Analyse for clinical terms">
                    <span style={{ lineHeight: 0 }}>
                      <IconButton
                        size="medium"
                        onClick={() =>
                          analyzeText(formValues.referralNotes, 'manual')
                        }
                        disabled={analyzingNote || isDisabled}
                      >
                        <AutoAwesomeIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </InputAdornment>
              ) : null
            }}
          />
        </Grid>

        <ClinicalSuggestionsPanel
          suggestions={suggestions}
          analyzingNote={analyzingNote}
          selectedSuggestions={selectedSuggestions}
          toggleSuggestion={toggleSuggestion}
          onAccept={() =>
            acceptSuggestions().then((result) => {
              if (result?.success) {
                const { count = {} } = result;
                const parts = [];
                if (count.phenotypes)
                  parts.push(`${count.phenotypes} phenotype(s)`);
                if (count.genotypes)
                  parts.push(`${count.genotypes} genotype(s)`);
                if (count.diseases) parts.push(`${count.diseases} disease(s)`);
                setSnack({
                  status: true,
                  msg: `Added ${parts.join(', ')} to patient record`,
                  severity: 'success'
                });
              } else {
                setSnack({
                  status: true,
                  msg: 'Failed to save suggestions',
                  severity: 'error'
                });
              }
            })
          }
          savingNote={savingNote}
          disabled={isDisabled}
        />

        <UploadFileList
          value={formValues.fileAttachments}
          familyId={familyMemberId}
          onChange={(value) =>
            setFormValues({
              ...formValues,
              fileAttachments: value
            })
          }
          readOnly={isDisabled}
          onUploadComplete={(uploadData) => {
            const text = uploadData?.extractedText;
            if (text) handleAttachmentText(text);
          }}
        />

        <Grid
          container
          size={12}
          sx={{
            alignItems: 'center'
          }}
        >
          <Grid
            size={{
              xs: 6,
              sm: 7
            }}
          >
            <FormLabel component="label" className="form-label">
              Show in family
            </FormLabel>
          </Grid>
          <Grid
            container
            size={{
              xs: 6,
              sm: 3
            }}
            sx={{
              alignItems: 'center'
            }}
          >
            <Grid>
              <Switch
                color="secondary"
                checked={formValues.showInFamily ?? false}
                onChange={onToggleSwitch}
                name="showInFamily"
                disabled={isDisabled}
              />
            </Grid>
            <Grid>
              <Typography>{formValues.showInFamily ? 'Yes' : 'No'}</Typography>
            </Grid>
          </Grid>
        </Grid>

        <Grid size={12}>
          <Grid
            container
            spacing={2}
            sx={{
              alignItems: 'center'
            }}
          >
            <Grid
              size={{
                xs: 12,
                sm: 'auto'
              }}
            >
              <FormLabel>Reminder</FormLabel>
            </Grid>
            <Grid>
              <Switch
                color="secondary"
                checked={formValues.reminderEnabled ?? false}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setFormValues((prev) => ({
                    ...prev,
                    reminderEnabled: enabled,
                    // when enabling, default to referralDate + 14 days (or today + 14 if none)
                    reminderDate: enabled
                      ? addDays(prev.referralDate || new Date(), 14)
                      : null
                  }));
                  if (!enabled) setReminderTouched(false);
                }}
                name="reminderEnabled"
                disabled={isDisabled}
              />
            </Grid>

            {formValues.reminderEnabled && (
              <Grid
                sx={{ marginLeft: 14 }}
                size={{
                  xs: 12,
                  sm: 6
                }}
              >
                <FormLabel>Reminder Date</FormLabel>
                <DateInput
                  name="reminderDate"
                  value={formValues.reminderDate || addDays(new Date(), 14)}
                  onChange={(val) => {
                    setReminderTouched(true); // user changed it manually
                    setFormValues((prev) => ({ ...prev, reminderDate: val }));
                  }}
                  variant="outline"
                  minDate={formValues.referralDate || new Date()}
                />
              </Grid>
            )}
          </Grid>
        </Grid>

        <ModalButtons
          hideSaveButton={isDisabled}
          isEditing={isEditing}
          hideContinueAdding={true}
          onClick={handleSubmit}
          handleClose={handleClose}
          intermediateBtnLabel="Delete"
          intermediateAction={isEditing && !isDisabled ? handleOpenModal : null}
          addLabel="Save New Referral"
        />
      </Grid>
    </form>
  );
}
